var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { isNil, useClasses, useControlledStateWarning, useLayoutProps, useProps, } from '@hawkins/helpers';
import { FormGroupContext } from '../formGroup';
import { Text } from '../text';
import { useToggleStyles } from './toggle.styles';
var displayName = 'Toggle';
/**
 * A binary component allowing the user to turn an option on or off.
 */
export var Toggle = React.forwardRef(function (props, ref) {
    var _a;
    // Props
    var _b = useProps(displayName, props), ariaLabel = _b["aria-label"], autoFocus = _b.autoFocus, checkedProp = _b.checked, className = _b.className, classesProp = _b.classes, component = _b.component, _c = _b.defaultChecked, defaultChecked = _c === void 0 ? false : _c, _d = _b.disabled, disabledProp = _d === void 0 ? false : _d, inputProps = _b.inputProps, inputRef = _b.inputRef, label = _b.label, labelProps = _b.labelProps, layout = _b.layout, name = _b.name, onChange = _b.onChange, readOnly = _b.readOnly, required = _b.required, styleProp = _b.style, tabIndex = _b.tabIndex, restProps = __rest(_b, ['aria-label', "autoFocus", "checked", "className", "classes", "component", "defaultChecked", "disabled", "inputProps", "inputRef", "label", "labelProps", "layout", "name", "onChange", "readOnly", "required", "style", "tabIndex"]);
    useControlledStateWarning(checkedProp, { displayName: displayName, stringifiedNullValue: 'false' });
    var hasLabel = !isNil(label);
    var isCheckedControlled = !isNil(checkedProp);
    var Component = component !== null && component !== void 0 ? component : (hasLabel ? 'label' : 'span');
    // State
    var _e = __read(React.useState(defaultChecked), 2), internalChecked = _e[0], setInternalChecked = _e[1];
    var checked = checkedProp !== null && checkedProp !== void 0 ? checkedProp : internalChecked;
    // Callbacks
    var inputOnChange = React.useCallback(function (ev) {
        var newChecked = ev.target.checked;
        if (!isCheckedControlled)
            setInternalChecked(newChecked);
        onChange === null || onChange === void 0 ? void 0 : onChange(ev, newChecked);
    }, [isCheckedControlled, onChange]);
    // Read from the form group context, if it is inside a <FormGroup/>
    var groupContext = React.useContext(FormGroupContext);
    var disabled = (groupContext === null || groupContext === void 0 ? void 0 : groupContext.disabled) || disabledProp;
    // Styles
    var baseClasses = useToggleStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root, (_a = {}, _a[classes.rootDisabled] = disabled, _a));
    var toggleIconOffClassName = clsx(classes.toggleIcon, classes.toggleIconOff);
    var toggleIconOnClassName = clsx(classes.toggleIcon, classes.toggleIconOn);
    return (_jsxs(Component, __assign({ className: rootClassName, ref: ref, style: style }, restProps, { children: [_jsxs("span", __assign({ className: classes.toggleContainer }, { children: [_jsx("input", __assign({ "aria-label": ariaLabel, autoFocus: autoFocus, checked: checked, className: classes.input, disabled: disabled, name: name, onChange: inputOnChange, readOnly: readOnly, ref: inputRef, required: required, tabIndex: tabIndex, type: "checkbox" }, inputProps)), _jsxs("span", __assign({ className: classes.track }, { children: [_jsx("svg", __assign({ "aria-hidden": true, className: toggleIconOnClassName, fill: "none", focusable: "false", height: "16", viewBox: "0 0 16 16", width: "16", xmlns: "http://www.w3.org/2000/svg" }, { children: _jsx("rect", { height: "5.25", stroke: "currentColor", strokeWidth: "0.75", width: "0.75", x: "9", y: "5.375" }) })), _jsx("svg", __assign({ "aria-hidden": true, className: toggleIconOffClassName, fill: "none", focusable: "false", height: "16", viewBox: "0 0 16 16", width: "16", xmlns: "http://www.w3.org/2000/svg" }, { children: _jsx("circle", { cx: "7", cy: "8", r: "2.25", stroke: "currentColor", strokeWidth: "1.5" }) })), _jsx("span", { className: classes.thumb })] })), _jsx("span", { 
                        // We set `opacity: 0` on the `input` to hide it,
                        // but this would prevent its focus ring from being visible,
                        // so we need a separate focus ring anchor.
                        className: classes.focusRingAnchor })] })), hasLabel && (_jsx(Text, __assign({ className: classes.label, colorVariant: disabled ? 'disabled' : 'default', component: "span" }, labelProps, { children: label })))] })));
});
Toggle.displayName = displayName;
